import cn from 'classnames';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import FormGroup, { FormGroupProps } from '../FormGroup/FormGroup';

export type FormFieldValue = string | number | undefined;

export type FormFieldProps = Pick<
  React.InputHTMLAttributes<HTMLInputElement>,
  'id' | 'type' | 'value' | 'onBlur' | 'placeholder'
> &
  FormGroupProps & {
    onChange: (val: FormFieldValue) => void;
    format?: (raw: string | undefined) => string;
  };

const FormField: FunctionComponent<FormFieldProps> = ({
  id,
  type,
  value,
  onChange,
  onBlur,
  placeholder,
  format,
  ...props
}) => {
  const isErrored = (props.touched ?? true) && (props.error ?? false);

  const onChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      type === 'number'
        ? onChange(event.target.valueAsNumber)
        : onChange(format ? format(event.target.value) : event.target.value);
    },
    [type, format, onChange],
  );

  const sanitisedValue = useMemo(
    () => (type === 'number' && isNaN(value as number) ? '' : value),
    [type, value],
  );

  return (
    <FormGroup {...props}>
      <input
        type={type ?? 'text'}
        className={cn(
          'jg-form-control',
          isErrored ? 'jg-form-control--error' : '',
        )}
        id={id}
        name={props.name}
        placeholder={placeholder}
        value={sanitisedValue}
        onChange={onChangeHandler}
        onBlur={onBlur}
      />
    </FormGroup>
  );
};

export default FormField;
